/**
 * StarFieldManager - Handles the night sky star field
 *
 * Stars are rendered as points in a spherical shell around the camera.
 * They fade in at night and fade out during the day based on ambient
 * light intensity.
 *
 * Responsibilities:
 * - Create the star field geometry with random positions
 * - Fade stars based on time of day (ambient light)
 * - Keep stars centered on camera
 */

import * as THREE from 'three';
import { STARS } from '../config/constants.js';

export class StarFieldManager {
    /**
     * Create a new StarFieldManager
     * @param {THREE.Scene} scene - The Three.js scene to add stars to
     * @param {THREE.Camera} camera - The camera to follow
     * @param {ScenePreloader} [preloader] - Optional preloader for progress tracking
     */
    constructor(scene, camera, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.starField = null;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('stars', {
            dispose: () => this.dispose()
        });

        // Configuration (from constants, can be overridden)
        this.enabled = STARS.ENABLED;
        this.count = STARS.COUNT;
        this.size = STARS.SIZE;
        this.minDistance = STARS.MIN_DISTANCE;
        this.maxDistance = STARS.MAX_DISTANCE;
        this.brightness = STARS.BRIGHTNESS;
        this.fadeThreshold = STARS.FADE_THRESHOLD;
    }

    /**
     * Initialize the star field
     * Call this after scene and camera are ready
     */
    initialize() {
        if (!this.enabled) {
            this._onPreloadComplete?.();
            return;
        }
        this.createStarField();
        this._onPreloadComplete?.();
    }

    /**
     * Create the star field with random positions in upper hemisphere
     * @private
     */
    createStarField() {
        const positions = [];
        const colors = [];
        const sizes = [];

        for (let i = 0; i < this.count; i++) {
            // Generate random point on sphere using spherical coordinates
            const theta = Math.random() * Math.PI * 2; // Azimuthal angle (0 to 2π)
            // Only generate stars in upper hemisphere
            const phi = Math.random() * (Math.PI / 2); // Polar angle (0 to π/2)

            // Random distance between min and max
            const distance = this.minDistance + Math.random() * (this.maxDistance - this.minDistance);

            // Convert spherical to Cartesian coordinates
            const x = distance * Math.sin(phi) * Math.cos(theta);
            const y = distance * Math.cos(phi); // Y is up, ensure it's always positive
            const z = distance * Math.sin(phi) * Math.sin(theta);

            positions.push(x, y, z);

            // Vary star colors slightly (mostly white with slight color variation)
            const starBrightness = 0.8 + Math.random() * 0.2; // 0.8 to 1.0
            colors.push(starBrightness, starBrightness, starBrightness);

            // Vary star sizes
            sizes.push(this.size * (0.5 + Math.random() * 1.5)); // 0.5x to 2x base size
        }

        // Create buffer geometry for stars
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));

        // Create point material with vertex colors
        const material = new THREE.PointsMaterial({
            size: this.size,
            vertexColors: true,
            transparent: true,
            opacity: this.brightness,
            sizeAttenuation: true,  // Stars get smaller with distance
            fog: false,             // Stars shouldn't be affected by fog
            depthTest: true,        // Enable depth testing so terrain occludes stars
            depthWrite: false       // Don't write to depth buffer
        });

        this.starField = new THREE.Points(geometry, material);
        this.starField.renderOrder = -2; // Render stars before sky sphere and terrain
        this.scene.add(this.starField);
    }

    /**
     * Update star visibility based on ambient light
     * Stars fade in at night and fade out during day
     * @param {number} ambientIntensity - Current ambient light intensity (0-1)
     */
    updateVisibility(ambientIntensity) {
        if (!this.starField || !this.enabled) return;

        // Calculate target opacity based on ambient light
        let targetOpacity = 0;

        // Fade range adjusted for shadow-based lighting (low ambient values)
        const fadeRange = 0.05;

        if (ambientIntensity < this.fadeThreshold) {
            // Full brightness when very dark
            targetOpacity = this.brightness;
        } else if (ambientIntensity < this.fadeThreshold + fadeRange) {
            // Fade out gradually as it gets brighter
            const fadeAmount = (ambientIntensity - this.fadeThreshold) / fadeRange;
            targetOpacity = this.brightness * (1 - fadeAmount);
        }
        // else targetOpacity stays 0 (stars hidden during bright day)

        // Smoothly interpolate opacity
        const currentOpacity = this.starField.material.opacity;
        this.starField.material.opacity = currentOpacity + (targetOpacity - currentOpacity) * 0.05;
    }

    /**
     * Update star position to follow camera
     * Call this every frame
     */
    update() {
        if (!this.starField) return;

        // Keep stars centered on camera
        this.starField.position.copy(this.camera.position);
    }

    /**
     * Clean up resources
     */
    dispose() {
        if (this.starField) {
            this.starField.geometry.dispose();
            this.starField.material.dispose();
            this.scene.remove(this.starField);
            this.starField = null;
        }
    }
}
